import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";

/**
* Button Component
* ----------------
* This custom button compontent extends the standard HTML button, but adds features for:
* - Button variants (primary, secondary, tertiary, ghost, destructive - add variants as needed)
* - Button sizes (small, medium, large, square)
* 
* All custom button stylings should be here, expect for edge cases and single use buttons.
* The cn function is a helper to merge the CSS classes together nicely.
*/


const cn = (...classes: (string | false | null | undefined)[]) => clsx(classes);

export const buttonVariants = cva(
  "rounded-md font-medium transition-colors focus:outline-none flex items-center justify-center", // base styles
  {
    variants: {
      variant: {
        primary: "bg-vBlack text-white hover:bg-vOffBlack",
        secondary: "bg-neutral-100 text-vBlack hover:bg-neutral-200",
        tertiary: "bg-transparent text-vBlack border border-vGray300 hover:underline",
        ghost: "bg-transparent text-vBlack hover:bg-vGray100",
        destructive: "bg-danger text-white hover:bg-dangerDark",
      },
      size: {
        small: "px-2 py-1 text-xs",
        medium: "px-5 py-3 text-sm",
        large: "px-6 py-3 text-base",
        square: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "medium",
    },
  }
);

// extends the standard HTML button, so that our custom buttom supports built-in props like onClick, type ect.
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size = "medium",
      asChild = false,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          buttonVariants({ variant, size }),
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";
